import { useState } from "react";
import { RefreshCw } from "lucide-react";
import { useFetch, Panel, Tag, PageHeader } from "@/components/Common";
import { CandleChart } from "@/components/CandleChart";
import { digits, fmtPrice } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";

const INTERVALS = ["5min", "15min", "1h", "4h", "1day"];
const OVERLAYS = ["ema20", "ema50", "ema200", "sma20", "sma50", "bb"];

function Snapshot({ s, symbol }) {
  if (!s) return null;
  const rows = [
    ["Score", s.score], ["Trend", s.trend], ["Close", fmtPrice(s.close, symbol)], ["RSI", s.rsi], ["ATR", fmtPrice(s.atr, symbol)],
    ["EMA20/50/200", `${fmtPrice(s.ema20, symbol)} / ${fmtPrice(s.ema50, symbol)} / ${fmtPrice(s.ema200, symbol)}`],
    ["Bollinger", s.bb_position], ["Support", fmtPrice(s.support, symbol)], ["Resistance", fmtPrice(s.resistance, symbol)], ["Strategy", s.strategy],
  ];
  return (
    <div className="space-y-2" data-testid="market-snapshot">
      {rows.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-3 border-b border-border/50 pb-1.5 text-sm"><span className="text-muted-foreground">{k}</span><span className="num text-right">{v}</span></div>
      ))}
      <div className="flex flex-wrap gap-1 pt-1">{s.patterns.length ? s.patterns.map((p) => <Tag key={p} tone={p.includes("bull") || p === "hammer" ? "up" : p === "doji" ? "muted" : "down"}>{p.replace(/_/g, " ")}</Tag>) : <span className="text-xs text-muted-foreground">No candlestick pattern on last bar</span>}</div>
    </div>
  );
}

export default function Markets() {
  const [symbol, setSymbol] = useState("EURUSD");
  const [interval, setInterval] = useState("1h");
  const [overlays, setOverlays] = useState({ ema20: true, ema50: true, ema200: false, sma20: false, sma50: false, bb: true });
  const { data: symbols } = useFetch("/markets/symbols");
  const { data, loading, reload } = useFetch(`/markets/candles?symbol=${symbol}&interval=${interval}`, [], 60000);

  return (
    <div>
      <PageHeader title="Markets" sub="Real-time candlestick charts with technical overlays" right={
        <div className="flex flex-wrap items-center gap-2">
          <Select value={symbol} onValueChange={setSymbol}>
            <SelectTrigger className="w-36" data-testid="market-symbol-selector"><SelectValue /></SelectTrigger>
            <SelectContent>{symbols?.map((s) => <SelectItem key={s.symbol} value={s.symbol} data-testid={`symbol-option-${s.symbol}`}>{s.symbol}</SelectItem>)}</SelectContent>
          </Select>
          <div className="flex rounded border" data-testid="interval-selector">
            {INTERVALS.map((i) => <button key={i} onClick={() => setInterval(i)} data-testid={`interval-${i}`} className={`px-3 py-2 font-mono text-xs transition-colors ${i === interval ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>{i}</button>)}
          </div>
          <Button variant="outline" size="icon" onClick={reload} data-testid="chart-refresh-button"><RefreshCw size={14} /></Button>
        </div>
      } />
      <div className="grid gap-4 xl:grid-cols-[1fr_320px]">
        <Panel title={`${symbol} · ${interval}`} right={<div className="flex gap-2">{data && <Tag tone="blue" testid="data-source-tag">{data.source}</Tag>}{data && <Tag tone={data.market_open ? "up" : "warn"}>{data.market_open ? "session open" : "session closed"}</Tag>}</div>}>
          <div className="mb-3 flex flex-wrap gap-4">
            {OVERLAYS.map((o) => (
              <label key={o} className="flex items-center gap-2 font-mono text-xs uppercase text-muted-foreground">
                <Switch checked={overlays[o]} onCheckedChange={(v) => setOverlays({ ...overlays, [o]: v })} data-testid={`overlay-toggle-${o}`} /> {o}
              </label>
            ))}
          </div>
          {loading && !data ? <div className="h-[660px] animate-pulse rounded bg-secondary" /> : <CandleChart data={data} overlays={overlays} digits={digits(symbol)} />}
        </Panel>
        <Panel title="Technical snapshot"><Snapshot s={data?.snapshot} symbol={symbol} /></Panel>
      </div>
    </div>
  );
}
